import { useForm } from "react-hook-form";
import { ContactCall } from "ui/assets/svg/ContactCall";
import { translate } from "ui/i18n";
import { PageHeader } from "ui/pages/app/components/PageHeader";
import { Inputs } from "ui/pages/inputsType";
import { useWindowWidth } from "ui/utils/hooks";
import { INSelect } from "ui/pages/app/components/inputs/INSelect";
import { PrimaryButton } from "ui/pages/app/components/buttons/PrimaryButton";
import { INTextarea } from "ui/pages/app/components/inputs/INTextarea";
import { Snackbar } from "ui/pages/snackbar";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { sendContactEmail } from "domain/auth/auth.actions";
import { EmailAttachment } from "domain/entities/auth.entities";
import { INFileInput } from "ui/pages/app/components/inputs/INFileInput";
import { useState } from "react";
import { HiOutlineX, HiPaperClip } from "react-icons/hi";
import { HorizontalDivider } from "ui/pages/app/components/HorizontalDivider";
import { selectIsAuthenticationLoading } from "domain/auth/auth.selectors";

export function Contact() {
  const isDesktop = useWindowWidth();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectIsAuthenticationLoading);
  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);

  const { register, handleSubmit, formState, reset } = useForm<Inputs>({
    mode: "onSubmit",
    defaultValues: {
      object: "",
      message: "",
    },
  });
  const { errors, isValid } = formState;

  const handleAddAttachment = async (files: FileList | "") => {
    if (files && files.length > 0) {
      const newFiles: EmailAttachment[] = await Promise.all(
        Array.from(files).map(
          (file) =>
            new Promise<EmailAttachment>((resolve) => {
              const reader = new FileReader();
              reader.onload = () => {
                const base64 = (reader.result as string).split(",")[1];
                resolve({
                  filename: file.name,
                  content: base64,
                  contentType: file.type,
                });
              };
              reader.readAsDataURL(file);
            })
        )
      );
      setAttachments([...attachments, ...newFiles]);
    }
  };

  const removeAttachment = (index: number) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  const handleFormSubmit = async (data: Inputs) => {
    const result = await dispatch(
      sendContactEmail({
        object: translate(
          `ns1:Contact.ObjectValues.${
            data.object as "Problem" | "Bug" | "Suggestion" | "Other"
          }`
        ),
        message: data.message,
        attachments,
      })
    ).unwrap();

    if (result.success) {
      Snackbar.showSuccess({
        title: translate("ns1:Contact.MessageSent"),
      });
      setAttachments([]);
      reset();
    }
  };

  return (
    <div className={`flex h-full w-full flex-row overflow-x-hidden`}>
      <div className="h-full w-full px-2 pt-2 md:px-12 md:pt-8">
        {!isDesktop && (
          <PageHeader
            title={translate("ns1:Routes.Contact")}
            className="mb-4"
          />
        )}
        <div className="flex w-full flex-col gap-4 rounded-[20px] bg-white p-6 md:flex-row md:gap-20 md:p-10">
          <div className="w-full max-w-[500px]">
            <p className="mb-1 text-base font-bold md:mb-2 md:text-xl">
              {translate("ns1:Contact.ContactForm")}
            </p>
            <p className="text-sm md:text-base">
              {translate("ns1:Contact.ContactFormText")}
            </p>
            <p className="text-sm md:text-base">
              {translate("ns1:Contact.ContactFormText2")}
            </p>
            <div className="mt-4 flex w-full justify-center md:mt-8">
              <ContactCall />
            </div>
          </div>
          <div className="w-full">
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              className="space-y-6"
            >
              <INSelect
                error={errors.object}
                label={translate("ns1:Input.Labels.Object") + "*"}
                placeholder={translate(
                  "ns1:Input.Placeholders.ChooseMessageObject"
                )}
                register={{
                  ...register("object", {
                    required: translate("ns1:Input.Errors.Required"),
                  }),
                }}
                className="w-full"
                options={[
                  {
                    label: translate("ns1:Contact.ObjectValues.Problem"),
                    value: "Problem",
                  },
                  {
                    label: translate("ns1:Contact.ObjectValues.Bug"),
                    value: "Bug",
                  },
                  {
                    label: translate("ns1:Contact.ObjectValues.Suggestion"),
                    value: "Suggestion",
                  },
                  {
                    label: translate("ns1:Contact.ObjectValues.Other"),
                    value: "Other",
                  },
                ]}
              />

              <INTextarea
                error={errors.message}
                className="w-full"
                label={translate("ns1:Input.Labels.YourMessage") + "*"}
                placeholder={translate(
                  "ns1:Input.Placeholders.WriteMessageHere"
                )}
                register={{
                  ...register("message", {
                    required: translate("ns1:Input.Errors.Required"),
                  }),
                }}
              />

              <INFileInput
                label={translate("ns1:Input.Labels.JoinFiles")}
                onChange={handleAddAttachment}
                optional
              />

              <div>
                {attachments.map((attachment, index) => (
                  <div key={`${attachment.filename}-${index}`}>
                    {index !== 0 && <HorizontalDivider />}
                    <div
                      key={index}
                      className="align-center flex flex items-center justify-between rounded-lg bg-secondaryGrey-100 p-4"
                    >
                      <div className="flex min-w-0 items-center">
                        <div className="me-2 h-fit w-[24px] text-darkGrey-700">
                          <HiPaperClip size={24} />
                        </div>
                        <span className="w-full text-base font-medium text-secondaryGrey-800 [overflow-wrap:anywhere]">
                          {attachment.filename}
                        </span>
                      </div>
                      <button
                        onClick={() => removeAttachment(index)}
                        className="ms-2"
                      >
                        <HiOutlineX size={24} className="text-darkGrey-700" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="!mt-6 md:!mt-10">
                {!isValid && (
                  <div className=" text-sm leading-5 text-darkGrey-500">
                    {translate("ns1:Contact.FillAllFields")}
                  </div>
                )}

                <PrimaryButton
                  height={isDesktop ? "52px" : "46px"}
                  className="!mt-4  w-full"
                  disabled={!isValid || isLoading}
                  type="submit"
                >
                  {translate("ns1:Button.Validate")}
                </PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
